import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useNotification } from "context/notification.context";
import { getVendorCatalog, bookWithVendor, linkVendorWaybill, voidVendorBooking, refreshVendorLabel, markVendorTransit, sendVendorTracking, API_ENDPOINT } from "network/service";

const CURRENCIES = ["INR", "USD", "GBP", "EUR", "AED"];

// WhatsApp number as the server will use it: 10 digits = Indian mobile (91
// added); 11-15 digits = already has its country code; anything else invalid.
const toWhatsApp = (v) => {
  const d = String(v || "").replace(/[^0-9]/g, "");
  if (d.length === 10) return `91${d}`;
  return d.length >= 11 && d.length <= 15 ? d : "";
};

const fmt = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

/**
 * Book a saved parcel with the courier vendor (Worldfirst / ICL) via the
 * Xpresion API. Deliberate action — creates a live shipment and returns the
 * vendor AWB + label/proforma PDFs.
 */
export default function VendorBooking(props) {
  const details = props.details || {};
  const { showNotification } = useNotification();

  const [catalog, setCatalog] = useState({ vendors: [], products: [], services: {} });
  const [loadingCat, setLoadingCat] = useState(false);
  const [booking, setBooking] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState({
    vendor_key: details.vendor_key || "",
    service: details.xpresion_service || "",
    carrier: details.xpresion_carrier || "",
    product: details.xpresion_product || "SPX",
    is_commercial: !!details.is_commercial,
    // INR unless the shipment was already booked in another currency (the
    // stored value is otherwise just the database default).
    currency: (details.vendor_awb && details.shipment_currency) || "INR",
  });
  // The ACTIVE booking (green box). A voided one is shown in red instead.
  const [result, setResult] = useState(
    details.vendor_awb && details.vendor_booking_status === 1
      ? {
          awb: details.vendor_awb,
          forwarding_no: details.vendor_forwarding_no,
          forwarding_nos: details.vendor_forwarding_nos,
          label_file: details.vendor_label_file,
          wfs_awb_file: details.vendor_wfs_awb_file,
          proforma_file: details.vendor_proforma_file,
          carrier_invoice_file: details.vendor_carrier_invoice_file,
          hold_remark: details.vendor_hold_remark,
        }
      : null
  );

  // Cancelled bookings (red boxes). Older shipments only kept the AWB.
  const [voids, setVoids] = useState(() => {
    const hist = Array.isArray(details.vendor_void_history) ? details.vendor_void_history : [];
    const seen = new Set(hist.map((v) => v.awb));
    const older = (details.vendor_voided_awbs || []).filter((a) => !seen.has(a)).map((awb) => ({ awb }));
    return [...older, ...hist];
  });

  // Customer tracking for the active booking: In Transit (no message) and
  // the tracking message — two separate, deliberate steps.
  const [shipStatus, setShipStatus] = useState(details.status);
  const [trackingNo, setTrackingNo] = useState(details.tracking_number || "");
  const [transitAt, setTransitAt] = useState(details.vendor_transit_at || null);
  const [sent, setSent] = useState(
    details.vendor_tracking_sent_at
      ? { at: details.vendor_tracking_sent_at, to: details.vendor_tracking_sent_to, no: details.vendor_tracking_sent_no, by: details.vendor_tracking_sent_by }
      : null
  );
  const [transitMsg, setTransitMsg] = useState("");
  const [marking, setMarking] = useState(false);
  const [sending, setSending] = useState(false);
  // Where the tracking goes: the sender's mobile on file unless staff change it.
  const senderOnFile = toWhatsApp(details.from && typeof details.from === "object" ? details.from.phone : "");
  const [sendTo, setSendTo] = useState(senderOnFile);
  const sendTarget = toWhatsApp(sendTo);
  // Every send attempt on this shipment (kept across voids / re-bookings).
  const [sendHistory, setSendHistory] = useState(Array.isArray(details.vendor_tracking_history) ? details.vendor_tracking_history : []);
  const leadNo = result ? result.forwarding_no || (result.forwarding_nos || [])[0] || "" : "";

  // A new or linked booking starts its own In Transit / sent steps.
  const startedBooking = (data) => {
    setResult(data);
    setTransitAt(null);
    setSent(null);
    if (data.forwarding_no) setTrackingNo(data.forwarding_no);
  };

  const markTransit = async () => {
    setMarking(true);
    try {
      const resp = await markVendorTransit({ id: details._id, message: transitMsg });
      setShipStatus(resp.data.status);
      setTrackingNo(resp.data.tracking_number);
      setTransitAt(resp.data.transit_at);
      showNotification("success", "In Transit", `Tracking ${resp.data.tracking_number} set — the customer has NOT been messaged`);
      props.handlesuccess && props.handlesuccess();
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Failed";
      showNotification("error", "Not updated", msg);
    } finally {
      setMarking(false);
    }
  };

  const sendTracking = async (force = false) => {
    // eslint-disable-next-line no-alert
    if (!sendTarget) return showNotification("error", "Check the number", "Enter a valid mobile number (10 digits, or with country code)");
    const other = senderOnFile && sendTarget !== senderOnFile ? " — this is NOT the sender's number on file" : "";
    // eslint-disable-next-line no-alert
    if (!force && !window.confirm(`Send tracking ${trackingNo} to ${sendTarget}${other} now (WhatsApp, SMS if that fails)?`)) return;
    setSending(true);
    try {
      const resp = await sendVendorTracking({ id: details._id, to: sendTarget, ...(force ? { force: true } : {}) });
      const d = resp.data;
      setSent({ at: d.vendor_tracking_sent_at, to: d.vendor_tracking_sent_to, no: d.vendor_tracking_sent_no, by: d.vendor_tracking_sent_by });
      if (Array.isArray(d.history)) setSendHistory(d.history);
      showNotification("success", "Tracking sent", `Sent ${d.vendor_tracking_sent_no} to ${d.vendor_tracking_sent_to} by ${d.notifyChannel === "sms" ? "SMS" : "WhatsApp"}`);
      props.handlesuccess && props.handlesuccess();
    } catch (err) {
      const data = (err && err.response && err.response.data) || {};
      if (Array.isArray(data.history)) setSendHistory(data.history); // failed attempts are logged too
      if (data.code === "ALREADY_SENT") {
        setSending(false);
        // eslint-disable-next-line no-alert
        if (window.confirm(`${data.message} on ${fmt(data.sent_at)}. Send it again?`)) await sendTracking(true);
        return;
      }
      showNotification("error", "Tracking not sent", data.message || (err && err.message) || "Failed");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    setLoadingCat(true);
    getVendorCatalog()
      .then((resp) => setCatalog(resp.data || { vendors: [], products: [], services: {} }))
      .catch((err) => {
        console.log(err);
        showNotification("error", "Error", "Failed to load vendor catalog");
      })
      .finally(() => setLoadingCat(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const services = (catalog.services && catalog.services[form.vendor_key]) || [];
  const products = catalog.products || [];
  const currentServiceIdx = services.findIndex(
    (s) => s.service === form.service && s.carrier === form.carrier
  );

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Fields the vendor would reject (checked against its API rules before
  // anything is sent) — listed in the popup so staff can fix them all at once.
  const [problems, setProblems] = useState([]);
  // Reference No is mandatory for the vendor; older shipments may not have one.
  const missingRef = !String(details.reference_no || "").trim();

  const submit = async (force = false) => {
    if (!form.vendor_key) return showNotification("error", "Error", "Select a vendor");
    if (!form.service) return showNotification("error", "Error", "Select a service");
    setProblems([]);
    setBooking(true);
    try {
      const resp = await bookWithVendor({ id: details._id, ...form, ...(force ? { force: true } : {}) });
      startedBooking(resp.data);
      showNotification(
        resp.data.hold_remark ? "warning" : "success",
        resp.data.notice ? "Booking linked" : "Booked",
        `Vendor AWB ${resp.data.awb}${resp.data.hold_remark ? ` — ON HOLD: ${resp.data.hold_remark}` : ""}`
      );
      props.handlesuccess && props.handlesuccess();
    } catch (err) {
      const data = (err && err.response && err.response.data) || {};
      // The previous attempt timed out and may have created a waybill. Only
      // book again once the user confirms they've checked the vendor portal.
      if (data.code === "UNCERTAIN_PREVIOUS") {
        setBooking(false);
        // eslint-disable-next-line no-alert
        if (window.confirm(`${data.message}

I've checked the portal and there is no booking for this shipment — book now?`)) {
          submit(true);
        }
        return;
      }
      if (Array.isArray(data.problems) && data.problems.length) {
        setProblems(data.problems);
        showNotification("error", "Not sent to the vendor", "Fix the fields listed in the popup, then book again");
        return;
      }
      // Surface the REAL vendor error message to the user.
      const msg = data.message || (err && err.message) || "Vendor booking failed";
      showNotification("error", "Booking failed", msg);
    } finally {
      setBooking(false);
    }
  };

  // Attach a waybill already created at the vendor (after a timeout, or booked
  // directly in the portal) — the server checks it really is this shipment.
  const [linkAwb, setLinkAwb] = useState("");
  const [linking, setLinking] = useState(false);
  const linkExisting = async () => {
    if (!linkAwb.trim()) return showNotification("error", "Error", "Enter the waybill number (e.g. CH4034454)");
    setLinking(true);
    try {
      const resp = await linkVendorWaybill({ id: details._id, awb: linkAwb.trim(), vendor_key: form.vendor_key });
      startedBooking(resp.data);
      setLinkAwb("");
      showNotification(resp.data.hold_remark ? "warning" : "success", "Waybill linked", resp.data.notice || `Linked ${resp.data.awb}`);
      props.handlesuccess && props.handlesuccess();
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || "Could not link the waybill";
      showNotification("error", "Link failed", msg);
    } finally {
      setLinking(false);
    }
  };

  const voidBooking = async () => {
    if (!result || !result.awb) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Cancel vendor AWB ${result.awb}? This voids the shipment.` +
        (sent ? `\n\nThe customer was already sent tracking ${sent.no} — after booking again, send them the new number.` : "")
    )) return;
    setVoiding(true);
    try {
      const resp = await voidVendorBooking({ id: details._id, awb: result.awb });
      setVoids((v) => [...v, (resp.data && resp.data.entry) || { awb: result.awb, voided_at: new Date() }]);
      setResult(null);
      setTransitAt(null);
      setSent(null);
      if (resp.data && resp.data.tracking_cleared) setTrackingNo("");
      showNotification("success", "Voided", `AWB ${result.awb} cancelled`);
      props.handlesuccess && props.handlesuccess();
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (err && err.message) ||
        "Void failed";
      showNotification("error", "Void failed", msg);
    } finally {
      setVoiding(false);
    }
  };

  const refreshLabel = async () => {
    setRefreshing(true);
    try {
      const resp = await refreshVendorLabel({ id: details._id });
      setResult((r) => ({ ...(r || {}), ...resp.data }));
      showNotification(
        "success",
        "Refreshed",
        resp.data.forwarding_no ? `Carrier AWB ${resp.data.forwarding_no}` : "Label updated"
      );
      props.handlesuccess && props.handlesuccess();
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (err && err.message) ||
        "Refresh failed";
      showNotification("error", "Refresh failed", msg);
    } finally {
      setRefreshing(false);
    }
  };

  const fileUrl = (name) => `${API_ENDPOINT}${name}`;

  return (
    <Dialog open={props.isopen} onClose={props.handleclose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Book with vendor — {details.tracking_number_internal || details.code || ""}
      </DialogTitle>
      <DialogContent dividers>
        {/* Cancelled bookings — kept for reference; the active booking (green) is below. */}
        {voids.map((v, i) => (
          <MDBox key={`${v.awb}-${i}`} mb={1.5} p={1.5} sx={{ border: "2px solid #e53935", borderRadius: 2, background: "#fff5f5" }}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDBox component="span" px={1} sx={{ background: "#e53935", color: "#fff", borderRadius: 1, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.05em" }}>
                VOIDED
              </MDBox>
              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#c62828", textDecoration: "line-through" }}>
                Vendor AWB: {v.awb}
              </MDTypography>
            </MDBox>
            {(v.forwarding_nos || []).length ? (
              <MDTypography variant="caption" display="block" sx={{ textDecoration: "line-through" }}>
                {v.carrier || "Carrier"} no{v.forwarding_nos.length > 1 ? "s" : ""}: {v.forwarding_nos.join("  ·  ")}
              </MDTypography>
            ) : null}
            <MDTypography variant="caption" display="block" color="text">
              {v.booked_at ? `Booked ${fmt(v.booked_at)} · ` : ""}
              {v.voided_at ? `Voided ${fmt(v.voided_at)}` : "Voided"}
              {v.voided_by ? ` by ${v.voided_by}` : ""}
            </MDTypography>
            {v.tracking_sent_at ? (
              <MDTypography variant="caption" display="block" fontWeight="bold" sx={{ color: "#c62828" }}>
                ⚠ Tracking {v.tracking_sent_no} had been sent to the customer ({v.tracking_sent_to}) on {fmt(v.tracking_sent_at)} — send them the new number.
              </MDTypography>
            ) : null}
          </MDBox>
        ))}

        {result && (
          <MDBox mb={2} p={2} sx={{ backgroundColor: "#e8f5e9", borderRadius: 2 }}>
            <MDTypography variant="button" fontWeight="bold" color="success">
              {voids.length ? "New booking" : "Booked"} — Vendor AWB: {result.awb}
            </MDTypography>
            {(result.forwarding_nos || []).length > 1 ? (
              <MDTypography variant="caption" display="block">
                Carrier forwarding nos (one per box):{" "}
                {result.forwarding_nos.map((n, i) => `Box ${i + 1}: ${n}`).join("  ·  ")}
              </MDTypography>
            ) : result.forwarding_no ? (
              <MDTypography variant="caption" display="block">
                Carrier forwarding no: {result.forwarding_no}
              </MDTypography>
            ) : null}
            {result.notice ? (
              <MDTypography variant="caption" display="block" color="info" fontWeight="medium">
                ℹ {result.notice}
              </MDTypography>
            ) : null}
            {result.hold_remark ? (
              <MDBox mt={1} p={1} sx={{ background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 1.5 }}>
                <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ color: "#e65100" }}>
                  ⚠ On hold at the vendor: {result.hold_remark}
                </MDTypography>
                <MDTypography variant="caption" display="block">
                  The waybill exists but won&apos;t move until the vendor releases it.
                </MDTypography>
              </MDBox>
            ) : null}
            <MDBox mt={1} display="flex" gap={1} flexWrap="wrap">
              {result.label_file ? (
                <MDButton
                  size="small"
                  color="info"
                  onClick={() => window.open(fileUrl(result.label_file), "_blank")}
                >
                  <Icon>download</Icon>&nbsp;UPS Waybill
                </MDButton>
              ) : null}
              {result.wfs_awb_file ? (
                <MDButton
                  size="small"
                  color="dark"
                  onClick={() => window.open(fileUrl(result.wfs_awb_file), "_blank")}
                >
                  <Icon>download</Icon>&nbsp;WFS AWB
                </MDButton>
              ) : null}
              {result.proforma_file ? (
                <MDButton
                  size="small"
                  color="secondary"
                  onClick={() => window.open(fileUrl(result.proforma_file), "_blank")}
                >
                  <Icon>download</Icon>&nbsp;WF Invoice
                </MDButton>
              ) : null}
              {/* The carrier's own Commercial Invoice (UPS), from the booking / Refresh. */}
              {result.carrier_invoice_file ? (
                <MDButton
                  size="small"
                  color="primary"
                  onClick={() => window.open(fileUrl(result.carrier_invoice_file), "_blank")}
                >
                  <Icon>download</Icon>&nbsp;{form.carrier || "Carrier"} Invoice
                </MDButton>
              ) : null}
              <MDButton size="small" color="warning" onClick={refreshLabel} disabled={refreshing}>
                <Icon>refresh</Icon>&nbsp;{refreshing ? "…" : "Refresh"}
              </MDButton>
              <MDButton size="small" color="error" onClick={voidBooking} disabled={voiding}>
                <Icon>cancel</Icon>&nbsp;{voiding ? "Voiding…" : "Void"}
              </MDButton>
            </MDBox>
            <Divider />
            {/* ── Customer tracking: two deliberate steps ── */}
            <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Customer tracking
            </MDTypography>
            <MDTypography variant="caption" display="block" color="text">
              Status: <strong>{{ 0: "Pending", 5: "Picked Up", 1: "In Transit", 2: "On Hold", 3: "Delivered", 4: "Issue" }[shipStatus] || "—"}</strong>
              {" · "}Tracking no: <strong>{trackingNo || "—"}</strong>
            </MDTypography>
            {!leadNo ? (
              <MDTypography variant="caption" display="block" color="warning">
                The carrier number isn&apos;t available yet — press Refresh.
              </MDTypography>
            ) : null}
            <MDBox mt={1} display="flex" alignItems="flex-start" gap={1} flexWrap="wrap">
              {/* 1) In Transit + tracking number — customer is NOT messaged */}
              {transitAt && shipStatus === 1 && trackingNo === leadNo ? (
                <MDTypography variant="caption" display="block" color="success" fontWeight="medium" sx={{ width: "100%" }}>
                  ✓ In Transit since {fmt(transitAt)} with {leadNo} (customer not messaged)
                </MDTypography>
              ) : (
                <>
                  {shipStatus !== 1 && shipStatus !== 3 ? (
                    <MDInput
                      size="small"
                      label="Timeline message (optional)"
                      placeholder={`Shipment in transit with ${form.carrier || "the carrier"} — tracking no. ${leadNo}`}
                      value={transitMsg}
                      onChange={(e) => setTransitMsg(e.target.value)}
                      sx={{ flex: "1 1 100%" }}
                    />
                  ) : null}
                  <MDButton size="small" color="info" variant="gradient" onClick={markTransit} disabled={marking || !leadNo || shipStatus === 3}>
                    <Icon>local_shipping</Icon>&nbsp;{marking ? "Updating…" : shipStatus === 1 ? `Use ${leadNo || "carrier no"} as tracking` : "Mark In Transit"}
                  </MDButton>
                </>
              )}
              {/* 2) Message the customer — only when staff press it, to the number shown */}
              <MDInput
                size="small"
                label="Send tracking to (WhatsApp)"
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
                error={!!sendTo && !sendTarget}
                helperText={
                  sendTo && !sendTarget
                    ? "Enter 10 digits, or the full number with country code"
                    : !senderOnFile
                      ? "No mobile on file for the sender — enter the number"
                      : sendTarget === senderOnFile
                        ? "Sender's mobile on file"
                        : `Different number — sender's mobile on file is ${senderOnFile}`
                }
                sx={{ flex: "1 1 220px" }}
              />
              <MDButton size="small" color="success" variant="gradient" onClick={() => sendTracking()}
                disabled={sending || !sendTarget || !trackingNo || shipStatus !== 1 || trackingNo !== leadNo}>
                <Icon>send</Icon>&nbsp;{sending ? "Sending…" : sent ? "Send tracking again" : "Send tracking to customer"}
              </MDButton>
            </MDBox>
            {sent ? (
              <MDTypography variant="caption" display="block" color="success" fontWeight="medium" mt={0.5}>
                ✓ Tracking {sent.no} sent to {sent.to} on {fmt(sent.at)}{sent.by ? ` by ${sent.by}` : ""}
              </MDTypography>
            ) : (
              <MDTypography variant="caption" display="block" color="text" mt={0.5}>
                Nothing is sent to the customer until you press &quot;Send tracking to customer&quot;.
              </MDTypography>
            )}
            <MDTypography variant="caption" display="block" color="error" mt={0.5}>
              To book again, void this booking first.
            </MDTypography>
          </MDBox>
        )}

        {sendHistory.length ? (
          <MDBox mb={2} p={1} sx={{ background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 1.5 }}>
            <MDTypography variant="caption" fontWeight="bold" display="block">
              Tracking sent history ({sendHistory.length})
            </MDTypography>
            {[...sendHistory].reverse().map((h, i) => (
              <MDTypography key={`${h.at}-${i}`} variant="caption" display="block"
                sx={{ color: h.status === "sent" ? "inherit" : "#c62828" }}>
                {fmt(h.at)} — {h.to || "no number"}{h.on_file ? " (sender on file)" : " (other number)"}
                {" · "}{h.tracking_no}{h.vendor_awb && h.vendor_awb !== (result && result.awb) ? ` (booking ${h.vendor_awb})` : ""}
                {" · "}{h.status === "sent" ? (h.channel === "sms" ? "SMS" : "WhatsApp") : `NOT sent (${h.status})`}
                {h.by ? ` · by ${h.by}` : ""}
              </MDTypography>
            ))}
          </MDBox>
        ) : null}

        {missingRef ? (
          <MDBox mb={2} p={1.5} sx={{ background: "#ffebee", border: "1px solid #e57373", borderRadius: 1.5 }}>
            <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ color: "#c62828" }}>
              ⚠ This shipment has no Reference No
            </MDTypography>
            <MDTypography variant="caption" display="block">
              The vendor requires it. Edit the shipment, enter the branch Reference No (e.g. WF141 - WM), then book.
            </MDTypography>
          </MDBox>
        ) : null}

        {problems.length ? (
          <MDBox mb={2} p={1.5} sx={{ background: "#ffebee", border: "1px solid #e57373", borderRadius: 1.5 }}>
            <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ color: "#c62828" }}>
              Not sent — the vendor would reject these. Fix them in the shipment (Edit), then book again:
            </MDTypography>
            <MDBox component="ul" m={0} pl={2.5}>
              {problems.map((p) => (
                <MDTypography key={p} component="li" variant="caption" display="list-item">
                  {p}
                </MDTypography>
              ))}
            </MDBox>
          </MDBox>
        ) : null}

        <Grid container spacing={2} mt={0}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                label="Vendor"
                value={form.vendor_key}
                sx={{ height: 44 }}
                onChange={(e) =>
                  setField({ vendor_key: e.target.value, service: "", carrier: "" })
                }
              >
                {(catalog.vendors || []).map((v) => (
                  <MenuItem key={v.key} value={v.key}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth disabled={!form.vendor_key}>
              <InputLabel>Service</InputLabel>
              <Select
                label="Service"
                value={currentServiceIdx >= 0 ? currentServiceIdx : ""}
                sx={{ height: 44 }}
                onChange={(e) => {
                  const s = services[e.target.value];
                  if (s) setField({ service: s.service, carrier: s.carrier });
                }}
              >
                {services.map((s, i) => (
                  <MenuItem key={`${s.service}-${s.carrier}-${i}`} value={i}>
                    {s.service} ({s.carrier})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Product</InputLabel>
              <Select
                label="Product"
                value={form.product}
                sx={{ height: 44 }}
                onChange={(e) => setField({ product: e.target.value })}
              >
                {products.map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    {p.name} ({p.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                label="Currency"
                value={form.currency}
                sx={{ height: 44 }}
                onChange={(e) => setField({ currency: e.target.value })}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_commercial}
                  color="info"
                  onChange={(e) => setField({ is_commercial: e.target.checked })}
                />
              }
              label={form.is_commercial ? "Commercial shipment" : "Personal shipment"}
            />
            {form.is_commercial ? (
              <MDTypography variant="caption" color="warning" display="block">
                Commercial (CSB-V) export uses per-item HSN/value + the customer&apos;s IEC/GST.
                Ensure the box items carry HSN, quantity and rate.
              </MDTypography>
            ) : null}
          </Grid>
        </Grid>
        {!result && (
          <MDBox mt={3} p={1.5} sx={{ border: "1px dashed #b0bec5", borderRadius: 2 }}>
            <MDTypography variant="button" fontWeight="bold" display="block">
              Already booked in the vendor portal?
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              If a waybill already exists for this shipment (e.g. after a timeout, or booked directly in the
              portal), link it here instead of booking again. We check it matches this shipment&apos;s sender and receiver.
            </MDTypography>
            <MDBox display="flex" gap={1} alignItems="center">
              <MDInput
                label="Vendor waybill (AWB)"
                placeholder="e.g. CH4034454"
                value={linkAwb}
                sx={{ flex: 1 }}
                onChange={(e) => setLinkAwb(e.target.value)}
              />
              <MDButton variant="outlined" color="info" onClick={linkExisting} disabled={linking || booking}>
                <Icon>link</Icon>&nbsp;{linking ? "Checking…" : "Link waybill"}
              </MDButton>
            </MDBox>
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={props.handleclose}>
          Close
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => submit()}
          disabled={booking || loadingCat || missingRef || !!result}
        >
          <Icon>local_shipping</Icon>&nbsp;
          {booking ? "Booking…" : voids.length ? "Book again" : "Book with vendor"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
